import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import type { BulkManageRow } from '../../types';
import { isBulkManageAxisRow, isBulkManageFundRow, isBulkManageStrategyRow, isBulkManageTotalRow } from '../../utils';
import { CellLoader } from 'venn-ui-kit';
import { itemHasNoReturns } from 'venn-utils';
import { emptyRangeTooltipCell } from '../tooltip-cells';
import { isNil } from 'lodash';
import React from 'react';
import { BulkManageAxisRowRangeCell } from './BulkManageAxisRowRangeCell';
import { RangeCellRendererPrimitives } from './RangeCellRendererPrimitives';
import { Match } from 'effect';
import { InvestmentRangeCell } from './InvestmentRangeCell';

type RangeRowValue = BulkManageRow;
export type RangeColDef = ColDef<BulkManageRow, RangeRowValue>;

type Props = ICellRendererParams<BulkManageRow, RangeRowValue> & {
  shouldShowHypotheticalReturnExtension: (row: BulkManageRow) => boolean;
};
export const RangeCellRenderer = ({ data, shouldShowHypotheticalReturnExtension }: Props) => {
  return Match.value(data).pipe(
    Match.when(isNil, () => null),
    Match.when(isBulkManageTotalRow, () => null),
    Match.when(isBulkManageAxisRow, (axisRow) => {
      if (!axisRow.commonRangeData) {
        return null;
      }
      const { earliestStart, latestEnd, frequency } = axisRow.commonRangeData;
      return <BulkManageAxisRowRangeCell earliestStart={earliestStart} latestEnd={latestEnd} frequency={frequency} />;
    }),
    Match.when(
      (row) => isBulkManageFundRow(row) && row.rangeLoading,
      () => <CellLoader />,
    ),
    Match.when(
      (row) => isBulkManageStrategyRow(row),
      ({ commonRangeData }) => {
        if (isNil(commonRangeData)) {
          return null;
        }
        const { overlap } = commonRangeData;
        return (
          <RangeCellRendererPrimitives.OverlapBar
            percentageWidth={overlap.percentageWidth}
            percentageStart={overlap.percentageStart}
          />
        );
      },
    ),
    Match.when(
      (row) => itemHasNoReturns(row.startDate, row.endDate),
      () => emptyRangeTooltipCell,
    ),
    Match.orElse((data) =>
      !isNil(data.commonRangeData) ? (
        <InvestmentRangeCell
          data={{
            ...data,
            commonRangeData: data.commonRangeData,
          }}
          shouldShowHypotheticalReturnExtension={shouldShowHypotheticalReturnExtension}
        />
      ) : (
        <CellLoader />
      ),
    ),
  );
};
